// The order a report lists athletes in when it lists them by group: youngest
// group first, then female before male, then last name, then first name.
//
// The Season Best report uses it always; the Athlete Season Points report uses
// it when "Sort by" is set to the group. The server's report files use the same
// rule (Services/Calculation/AthleteReportOrder.cs), so a file lists athletes in
// the order its table shows them. athleteOrder.test.js holds this file to the
// same cases as the server's tests.
//
// The group is the stored group number, which runs youngest to oldest for an age
// club and for a year-level club alike. Names are compared without regard to case.

// The value of the Athlete Season Points "Sort by" choice, and of the "sortBy"
// its two files are asked for with.
export const SORT_BY = { POINTS: 'points', GROUP: 'group' };

const genderRank = (gender) => (gender === 'F' ? 0 : 1);

const compareNames = (a, b) =>
  String(a ?? '').toLowerCase().localeCompare(String(b ?? '').toLowerCase());

// A new array in that order; the rows given are not changed. `fields` says how
// to read a row: { group, gender, lastName, firstName }, each a function.
export const sortByGroupGenderName = (rows, { group, gender, lastName, firstName }) =>
  [...rows].sort(
    (a, b) =>
      Number(group(a)) - Number(group(b)) ||
      genderRank(gender(a)) - genderRank(gender(b)) ||
      compareNames(lastName(a), lastName(b)) ||
      compareNames(firstName(a), firstName(b))
  );

// The Season Best report's order, which lists several events at once: each event
// in the club's order, and within an event the group order above (Doug,
// 2026-10-25). The server's report files use the same rule
// (AthleteReportOrder.ByEventThenGroupThenGenderThenName). `fields` is as above,
// plus `event`: the event's place in the club's order, a number.
export const sortByEventGroupGenderName = (rows, { event, ...groupFields }) => {
  const byGroup = sortByGroupGenderName(rows, groupFields);
  // sort is stable, so the group order holds within each event.
  return byGroup.sort((a, b) => Number(event(a)) - Number(event(b)));
};

// The order an athlete picker lists athletes in: last name, then first name, then
// club number (Doug, 2026-10-23: the Edit Results athlete list "should be by last
// name"). The apps list athletes the same way. A new array; the rows given are not
// changed. `fields` says how to read a row: { lastName, firstName, number }.
export const sortByName = (rows, { lastName, firstName, number }) =>
  [...rows].sort(
    (a, b) =>
      compareNames(lastName(a), lastName(b)) ||
      compareNames(firstName(a), firstName(b)) ||
      Number(number(a) ?? 0) - Number(number(b) ?? 0)
  );
